import { useTranslation } from '@kostbar/i18n'
import { KontrolleLine } from './period-status'
import { CloseMarker } from './close-list'
import type {
  ClosingClemens,
  ClosingPeriodReport,
} from '@/features/reports/api/use-closing-report-query'
import { vatRateLabel } from '@/features/reports/lib/accounting-labels'
import {
  formatCurrency,
  formatWeekdayDateTime,
} from '@/features/reports/lib/format'
import { cn } from '@/lib/utils'

const GROUPS = ['staff', 'selfCheckout', 'online'] as const

const HEAD = 'px-3 py-1.5 text-xs font-medium text-muted-foreground'
const NUM = 'whitespace-nowrap px-3 py-1 text-right tabular-nums'
/** The close column stays in view while the wide table scrolls sideways; it needs its own opaque ground. */
const STICKY = 'sticky left-0 z-10 border-r border-border'

/** The column definitions under the table, in the table's order. */
const NOTES = [
  ['groups.staff', 'staff'],
  ['groups.selfCheckout', 'selfCheckout'],
  ['groups.online', 'online'],
  ['columns.unbar', 'unbar'],
  ['columns.bons', 'bons'],
  ['columns.bankomat', 'bankomat'],
  ['columns.barumsatz', 'barumsatz'],
] as const

/**
 * Ansicht Clemens — the controller's monthly sheet, made by the system
 * (CHG-2026-10-accounting-close-periods, D4 and D11).
 *
 * Three figures on top (Umsatz, number of Tagesabschlüsse, the Kontrolle), then one table across
 * the full width: one row per close and a sum row, in the sheet's column order, with the column
 * meanings stated underneath. It scrolls sideways on a narrow screen rather than wrapping a figure,
 * with the close column held in place so every row stays identifiable.
 * The sections of the Übersicht are not repeated here.
 */
export function ClemensView({
  report,
  onSelect,
}: {
  report: ClosingPeriodReport
  onSelect: (ref: string) => void
}) {
  const { t } = useTranslation('reports')
  const rates = report.clemensRates

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <KeyFigure label={t('accounting.clemens.umsatz')}>
          <span className="text-2xl font-bold tabular-nums">
            {formatCurrency(report.clemensSum.umsatz)}
          </span>
        </KeyFigure>
        <KeyFigure label={t('accounting.clemens.closes')}>
          <span className="text-2xl font-bold tabular-nums">
            {report.period.closeCount}
          </span>
        </KeyFigure>
        <KeyFigure label={t('accounting.clemens.kontrolle')}>
          <KontrolleLine kontrolle={report.kontrolle} onSelect={onSelect} />
        </KeyFigure>
      </div>

      <div
        className="overflow-x-auto rounded-xl border border-border"
        data-print-fit
      >
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr className="border-b border-border">
              <th
                scope="col"
                rowSpan={2}
                className={cn(HEAD, STICKY, 'bg-muted text-left')}
              >
                {t('accounting.clemens.columns.closedAt')}
              </th>
              <th scope="col" rowSpan={2} className={cn(HEAD, 'text-right')}>
                {t('accounting.clemens.columns.umsatz')}
              </th>
              {GROUPS.map((group) => (
                <th
                  key={group}
                  scope="colgroup"
                  colSpan={rates.length}
                  className={cn(HEAD, 'border-l border-border text-center')}
                >
                  {t(`accounting.clemens.groups.${group}`)}
                </th>
              ))}
              {(
                ['unbar', 'bons', 'bankomat', 'barumsatz', 'trinkgeld'] as const
              ).map((column, index) => (
                <th
                  key={column}
                  scope="col"
                  rowSpan={2}
                  className={cn(
                    HEAD,
                    'text-right',
                    index === 0 && 'border-l border-border',
                  )}
                >
                  {t(`accounting.clemens.columns.${column}`)}
                </th>
              ))}
            </tr>
            <tr className="border-b border-border">
              {GROUPS.flatMap((group) =>
                rates.map((code, index) => (
                  <th
                    key={`${group}-${code}`}
                    scope="col"
                    className={cn(
                      HEAD,
                      'text-right',
                      index === 0 && 'border-l border-border',
                    )}
                  >
                    {vatRateLabel(t, code)}
                  </th>
                )),
              )}
            </tr>
          </thead>
          <tbody>
            {report.closes.map((row) => (
              <tr
                key={row.ref}
                className="group cursor-pointer hover:bg-muted"
                onClick={() => onSelect(row.ref)}
              >
                <td
                  className={cn(
                    STICKY,
                    'whitespace-nowrap bg-background px-3 py-1 group-hover:bg-muted',
                  )}
                >
                  <button
                    type="button"
                    className="text-left tabular-nums hover:underline"
                    aria-label={t('accounting.closes.open', {
                      at: formatWeekdayDateTime(row.closedAt),
                    })}
                    onClick={(event) => {
                      event.stopPropagation()
                      onSelect(row.ref)
                    }}
                  >
                    {formatWeekdayDateTime(row.closedAt)}
                  </button>
                  <CloseMarker close={row} />
                </td>
                <ClemensCells clemens={row.clemens} rates={rates} />
              </tr>
            ))}
            <tr className="border-t-2 border-border font-semibold">
              <td className={cn(STICKY, 'bg-background px-3 py-1.5')}>
                {t('accounting.total')}
              </td>
              <ClemensCells clemens={report.clemensSum} rates={rates} />
            </tr>
          </tbody>
        </table>
      </div>

      <section className="space-y-2 text-xs text-muted-foreground">
        <h3 className="font-medium text-foreground">
          {t('accounting.clemens.notes.title')}
        </h3>
        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1">
          {NOTES.map(([term, key]) => (
            <div key={key} className="contents">
              <dt className="font-medium text-foreground">
                {t(`accounting.clemens.${term}`)}
              </dt>
              <dd>{t(`accounting.clemens.notes.${key}`)}</dd>
            </div>
          ))}
        </dl>
        <p>{t('accounting.clemens.notes.check')}</p>
      </section>
    </div>
  )
}

function ClemensCells({
  clemens,
  rates,
}: {
  clemens: ClosingClemens
  rates: Array<string>
}) {
  return (
    <>
      <td className={cn(NUM, 'font-semibold')}>
        {formatCurrency(clemens.umsatz)}
      </td>
      {GROUPS.flatMap((group) =>
        rates.map((code, index) => (
          <td
            key={`${group}-${code}`}
            className={cn(NUM, index === 0 && 'border-l border-border')}
          >
            {formatCurrency(clemens[group][code] ?? 0)}
          </td>
        )),
      )}
      <td className={cn(NUM, 'border-l border-border')}>
        {formatCurrency(clemens.unbar)}
      </td>
      <td className={NUM}>{formatCurrency(clemens.bons)}</td>
      <td className={NUM}>{formatCurrency(clemens.bankomat)}</td>
      <td className={NUM}>{formatCurrency(clemens.barumsatz)}</td>
      <td className={NUM}>{formatCurrency(clemens.trinkgeld)}</td>
    </>
  )
}

function KeyFigure({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-xl border border-border px-4 py-3">
      <div className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-1">{children}</div>
    </div>
  )
}
